import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { Search, Loader2, Eye, Download } from 'lucide-react'
import { apiGet } from '../lib/api'

interface ClientesData {
  total: number
  page: number
  pageSize: number
  pages: number
  data: Array<{
    id: string
    nombre: string
    email: string
    telefono: string
    nroDoc: string
    ciudad: string
    provincia: string
    pais: string
    descuento: number
  }>
}

export function Clientes() {
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const { data, isLoading, isFetching } = useQuery<ClientesData>({
    queryKey: ['clientes', page, searchTerm],
    queryFn: () =>
      apiGet(
        `/api/admin/clientes?page=${page}${searchTerm ? `&q=${encodeURIComponent(searchTerm)}` : ''}`,
      ),
    placeholderData: (prev) => prev,
  })

  function onSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setSearchTerm(q.trim())
  }

  return (
    <>
      <Topbar
        title="Clientes"
        subtitle={
          data ? `${data.total.toLocaleString('es-MX')} clientes registrados` : 'Cargando…'
        }
        actions={
          <button className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50">
            <Download className="size-3.5" /> Exportar
          </button>
        }
      />
      <div className="p-6">
        <form onSubmit={onSearch} className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre, email o teléfono…"
              className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
            />
          </div>
          <button
            type="submit"
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Buscar
          </button>
        </form>

        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando clientes…</p>
            </div>
          )}

          {data && (
            <>
              <div className="relative overflow-x-auto">
                {isFetching && (
                  <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                    <Loader2 className="size-4 animate-spin text-brand-600" />
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="px-4 py-3 font-medium">Cliente</th>
                      <th className="px-4 py-3 font-medium">Contacto</th>
                      <th className="px-4 py-3 font-medium">Documento</th>
                      <th className="px-4 py-3 font-medium">Ubicación</th>
                      <th className="px-4 py-3 font-medium text-right">Descuento</th>
                      <th className="w-12 px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.map((c) => (
                      <tr
                        key={c.id}
                        className="border-t border-ink-100 transition-colors hover:bg-ink-50"
                      >
                        <td className="px-4 py-3">
                          <Link
                            to="/clientes/$id"
                            params={{ id: c.id }}
                            className="font-medium text-ink-900 hover:text-brand-600"
                          >
                            {c.nombre || '(sin nombre)'}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <div className="truncate text-ink-700">{c.email}</div>
                          <div className="text-xs text-ink-500">{c.telefono || '—'}</div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-ink-700">
                          {c.nroDoc || '—'}
                        </td>
                        <td className="px-4 py-3 text-ink-700">
                          <div>{c.ciudad || '—'}</div>
                          <div className="text-[10px] text-ink-500">
                            {c.provincia} · {c.pais}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {c.descuento > 0 ? (
                            <Badge variant="success">{c.descuento}%</Badge>
                          ) : (
                            <span className="text-xs text-ink-500">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to="/clientes/$id"
                            params={{ id: c.id }}
                            className="inline-flex rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                          >
                            <Eye className="size-4" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
                <div className="text-ink-500">
                  Página <strong className="text-ink-900">{data.page}</strong> de{' '}
                  <strong className="text-ink-900">{data.pages.toLocaleString('es-MX')}</strong>{' '}
                  ({data.total.toLocaleString('es-MX')} clientes)
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button className="rounded-md bg-brand-600 px-3 py-1 text-white">{page}</button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= data.pages}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
